import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  clearRenataWorkspaceFocus,
  consumeRenataFieldApply,
  getRenataWorkspaceFocus,
  setRenataWorkspaceFocus,
} from "@/lib/renata/workspace-focus";
import { fieldLabelEs } from "@/lib/renata/field-draft";
import { ProductosTour } from "@/components/ProductTour";
import { ClinicalPageShell } from "@/components/clinical";
import { DataTable, FilterBar, Loading } from "@/components/ds";
import { CrudShellHeader, CrudEmptyState } from "@/components/ds/CrudShell";
import type { DataTableColumn } from "@/components/ds";
import { ImageUploadField } from "@/components/ImageUploadField";
import { ProductGalleryField } from "@/components/ProductGalleryField";
import { SmartMatchChip } from "@/components/media/SmartMatchChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { WorkspaceMasterDetail, WorkspaceSectionTitle } from "@/components/workspace/WorkspaceMasterDetail";
import { FieldLabel } from "@/components/help/FieldHelpTip";
import { FeatureLockedState } from "@/components/workspace/FeatureLockedState";
import type { PlanApiErrorPayload } from "@structura/core/plan-api-errors";
import { Download,
  Layers,
  Loader2,
  Package,
  Pencil,
  Plus,
  Scale,
  Search,
  ShoppingBasket,
  SlidersHorizontal,
  Tag,
  Trash2,
  Warehouse,
  X, } from "lucide-react";
import { RenataLauncher } from "@/components/renata/RenataLauncher";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { apiGetJson, apiSendJson, apiDelete, clientRequestHeaders } from "@/lib/sm-api";
import { roundPrice, type PriceRoundingMode } from "@/lib/price-rounding";
import { QuotaLimitModal, type QuotaLimitPayload } from "@/components/QuotaLimitModal";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { useIsAdminCapable } from "@/hooks/use-role-capabilities";
import { useTenantContext } from "@/lib/tenant-context";
import { toast } from "sonner";
import { ProductTagPicker } from "@/components/ProductTagPicker";
import { ProductCatalogCoreSection } from "@/components/ProductCatalogCoreSection";
import { ProductRecipePanel } from "@/components/ProductRecipePanel";
import { ProductSupplierPanel } from "@/components/ProductSupplierPanel";
import { ProductPresentationsPanel } from "@/components/ProductPresentationsPanel";
import { businessAllowsMerchantPersonalization, businessAllowsSizeGuide, businessIsFoodMarket } from "@/lib/business-profile";
import {
  inventoryControlsIngredients,
  inventoryNavLabel,
  resolveStockControlMode,
} from "@/lib/stock-model";
import type { TipoVenta, ProductType } from "@/domain/sm-models";
import { evaluateProductPricing, type PricingAlert } from "@/lib/tax";
import type { ChannelMembershipForProduct } from "@/lib/db-service/catalog";
import { AlertTriangle, Info, Link2, Unlink } from "lucide-react";
import { PRODUCT_TYPE_LABELS } from "@/domain/sm-models";
import { DigitalAssetsSection } from "@/components/DigitalAssetsSection";
import { productTypeRequiresShipping } from "@structura/core/digital-assets";
import { MerchantExperienceBar } from "@/components/renata/MerchantExperienceBar";
import { parseDecimalQtyInput, sanitizeDecimalQtyInput } from "@/lib/decimal-qty-input";

export const Route = createFileRoute("/app/productos")({
  component: ProductosPage,
  head: () => ({ meta: [{ title: "Productos — Structura Market" }] }),
});

interface Categoria {
  id: string;
  name: string;
  active: boolean;
}

interface Brand {
  id: string;
  name: string;
}

/** SCRUM-102: opción liviana para el selector — el editor de producto no necesita el content completo. */
interface SizeGuideOption {
  id: string;
  name: string;
  productCount?: number;
}

interface Product {
  id: string;
  name: string;
  slug: string;
  categoriaId: string | null;
  categoriaName: string | null;
  brandId: string | null;
  brandName: string | null;
  description: string | null;
  longDescription: string | null;
  imagenUrl: string | null;
  tipoVenta: TipoVenta;
  sku?: string | null;
  ean?: string | null;
  priceMode?: "net" | "final";
  taxCategory?: "taxable" | "exempt" | "non_taxable" | "not_subject";
  taxRate?: number | null;
  costoReferencia?: number | null;
  markupPct?: number | null;
  costSourceProductId?: string | null;
  costMultiplier?: number | null;
  precioUnitario: number | null;
  precioPorKg: number | null;
  unidadMedida: string;
  minQty?: number | null;
  qtyStep?: number | null;
  stockActual: number | null;
  stockMinimo: number | null;
  tags: string[];
  tiempoPreparacionMinutos: number;
  activo: boolean;
  sortOrder: number;
  createdAt: string;
  trackStock?: boolean;
  isAddon?: boolean;
  bundleOnly?: boolean;
  isPersonalizable?: boolean;
  variantAxes?: Array<{ name: string; values: string[] }>;
  weightGrams?: number | null;
  productType?: ProductType;
  sizeGuideId?: string | null;
}

const UNIDADES_MEDIDA = ["kg", "g", "ml", "l", "unidad", "docena", "pack"] as const;
const PRODUCTS_PAGE_SIZE = 48;

interface ProductsPageResponse {
  items: Product[];
  total: number;
  hasMore: boolean;
}

const EMPTY_FORM = {
  name: "",
  description: "",
  longDescription: "",
  categoriaId: "",
  brandId: "",
  tipoVenta: "unidad" as TipoVenta,
  sku: "",
  ean: "",
  priceMode: "final" as "net" | "final",
  taxCategory: "taxable" as "taxable" | "exempt" | "non_taxable" | "not_subject",
  taxRate: "21",
  costoReferencia: "",
  markupPct: "",
  costSourceProductId: "",
  costMultiplier: "",
  precioUnitario: "",
  precioPorKg: "",
  unidadMedida: "kg",
  minQty: "",
  qtyStep: "",
  stockActual: "",
  stockMinimo: "",
  imagenUrl: "",
  tags: [] as string[],
  tiempoPreparacionMinutos: "",
  sortOrder: "0",
  trackStock: false,
  isAddon: false,
  bundleOnly: false,
  isPersonalizable: false,
  variantAxes: [] as Array<{ name: string; values: string[] }>,
  weightGrams: "",
  productType: "physical" as ProductType,
  sizeGuideId: "",
};

function pesos(n: number | null): string {
  if (n === null) return "—";
  return `$${n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function ProductosPage() {
  const tid = useWorkspaceTenantId();
  const canMutate = useIsAdminCapable();
  const { businessProfile, productFlags: ctxFlags } = useTenantContext();
  const isGastro = businessIsFoodMarket(businessProfile);
  const bomEnabled = ctxFlags?.inventory_bom === true;
  const suppliersEnabled = ctxFlags?.operations_suppliers === true;
  const stockMode = resolveStockControlMode(businessProfile, {
    inventoryBom: bomEnabled,
  });
  const hideUnitStock = inventoryControlsIngredients(stockMode);
  const merchantEnabled =
    ctxFlags?.integrations_merchant === true &&
    businessAllowsMerchantPersonalization(businessProfile);
  // SCRUM-416: Guía de talles es una capacidad comercial de indumentaria, no
  // una config genérica — no debe aparecer en food/market/otros nichos.
  const sizeGuideEnabled = businessAllowsSizeGuide(businessProfile);

  const [products, setProducts]       = useState<Product[]>([]);
  const [productsTotal, setProductsTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [cats, setCats]               = useState<Categoria[]>([]);
  const [brands, setBrands]           = useState<Brand[]>([]);
  const [sizeGuides, setSizeGuides]   = useState<SizeGuideOption[]>([]);
  const [creatingSizeGuide, setCreatingSizeGuide] = useState(false);
  const [newSizeGuideName, setNewSizeGuideName]   = useState("");
  const [savingSizeGuide, setSavingSizeGuide]     = useState(false);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [search, setSearch]           = useState("");
  const [filterCat, setFilterCat]     = useState<string>("all");
  const [filterStockStatus, setFilterStockStatus] = useState<"all" | "out" | "in" | "low">("all");
  const [filterTrackStock, setFilterTrackStock] = useState<"all" | "yes" | "no">("all");
  const [filterAddon, setFilterAddon] = useState<"all" | "yes" | "no">("all");
  const [filterActivo, setFilterActivo] = useState<"all" | "true" | "false">("all");
  const [filterBrand, setFilterBrand] = useState<string>("all");
  const [filterTipoVenta, setFilterTipoVenta] = useState<"all" | "unidad" | "peso">("all");
  const [filterProductType, setFilterProductType] = useState<"all" | "physical" | "digital" | "service" | "hybrid">("all");
  const [filterPersonalizable, setFilterPersonalizable] = useState<"all" | "yes" | "no">("all");
  const [filterBundleOnly, setFilterBundleOnly] = useState<"all" | "yes" | "no">("all");
  const [filterPriceAlert, setFilterPriceAlert] = useState<"all" | "yes" | "no">("all");
  const [filterTag, setFilterTag] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [editorOpen, setEditorOpen]   = useState(false);
  /** SCRUM-150: tab activo en el editor. "esencial" abierto por defecto. */
  const [editorTab, setEditorTab]     = useState<"esencial" | "avanzado" | "contenido">("esencial");
  const [editing, setEditing]         = useState<Product | null>(null);
  /** SCRUM-414: gestión rápida de imágenes desde el listado, sin abrir la ficha completa. */
  const [galleryPickerProduct, setGalleryPickerProduct] = useState<Product | null>(null);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [costSourceQuery, setCostSourceQuery] = useState("");
  const [costSourceMatches, setCostSourceMatches] = useState<{ id: string; name: string; costoReferencia: number | null }[]>([]);
  const [costSourceSearching, setCostSourceSearching] = useState(false);
  const [showCostSourceSuggestions, setShowCostSourceSuggestions] = useState(false);
  const [costSourceName, setCostSourceName] = useState<string | null>(null);
  const [costSourceCost, setCostSourceCost] = useState<number | null>(null);
  const [channelMemberships, setChannelMemberships] = useState<ChannelMembershipForProduct[]>([]);
  const [membershipsLoading, setMembershipsLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Product | null>(null);
  const [quotaModal, setQuotaModal] = useState<QuotaLimitPayload | null>(null);
  const [planLock, setPlanLock] = useState<PlanApiErrorPayload | null>(null);

  const hasExtraFilters =
    filterStockStatus !== "all"
    || filterTrackStock !== "all"
    || filterAddon !== "all"
    || filterActivo !== "all"
    || filterBrand !== "all"
    || filterTipoVenta !== "all"
    || filterProductType !== "all"
    || filterPersonalizable !== "all"
    || filterBundleOnly !== "all"
    || filterPriceAlert !== "all"
    || Boolean(filterTag.trim());

  const hasAnyFilter = Boolean(search) || filterCat !== "all" || hasExtraFilters;

  const clearProductFilters = () => {
    setSearch("");
    setFilterCat("all");
    setFilterStockStatus("all");
    setFilterTrackStock("all");
    setFilterAddon("all");
    setFilterActivo("all");
    setFilterBrand("all");
    setFilterTipoVenta("all");
    setFilterProductType("all");
    setFilterPersonalizable("all");
    setFilterBundleOnly("all");
    setFilterPriceAlert("all");
    setFilterTag("");
  };

  const loadProducts = () => {
    if (!tid) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("q", search);
    if (filterCat !== "all") params.set("categoriaId", filterCat);
    if (filterBrand !== "all") params.set("brandId", filterBrand);
    if (filterStockStatus !== "all") params.set("stockStatus", filterStockStatus);
    if (filterTrackStock !== "all") params.set("trackStock", filterTrackStock);
    if (filterAddon !== "all") params.set("isAddon", filterAddon);
    if (filterActivo !== "all") params.set("activo", filterActivo);
    if (filterTipoVenta !== "all") params.set("tipoVenta", filterTipoVenta);
    if (filterProductType !== "all") params.set("productType", filterProductType);
    if (filterPersonalizable !== "all") params.set("isPersonalizable", filterPersonalizable);
    if (filterBundleOnly !== "all") params.set("bundleOnly", filterBundleOnly);
    if (filterPriceAlert !== "all") params.set("priceAlert", filterPriceAlert);
    if (filterTag.trim()) params.set("tag", filterTag.trim());
    params.set("limit", String(PRODUCTS_PAGE_SIZE));
    params.set("offset", String((page - 1) * PRODUCTS_PAGE_SIZE));
    const qs = `?${params}`;
    apiGetJson<ProductsPageResponse>(`/api/products${qs}`, tid)
      .then((res) => {
        setProducts(res.items);
        setProductsTotal(res.total);
      })
      .catch(() => toast.error("Error al cargar productos"))
      .finally(() => setLoading(false));
  };

  const loadCats = () => {
    if (!tid) return;
    apiGetJson<Categoria[]>("/api/categories", tid)
      .then(data => setCats(data.filter(c => c.active)))
      .catch(() => {});
  };

  const loadBrands = () => {
    if (!tid) return;
    apiGetJson<Brand[]>("/api/brands", tid).then(setBrands).catch(() => {});
  };

  const loadSizeGuides = () => {
    if (!tid) return;
    apiGetJson<{ guides: SizeGuideOption[] }>("/api/size-guides", tid)
      .then((d) => setSizeGuides(d.guides ?? []))
      .catch(() => {});
  };

  const [priceRounding, setPriceRounding] = useState<PriceRoundingMode>("none");

  useEffect(() => {
    loadCats();
    loadBrands();
    loadSizeGuides();
    if (!tid) return;
    apiGetJson<{ config?: { priceRounding?: PriceRoundingMode } }>("/api/tenant/config", tid)
      .then((d) => setPriceRounding(d.config?.priceRounding ?? "none"))
      .catch(() => {});
  }, [tid]);

  const exportCsv = async () => {
    if (!tid) return;
    try {
      const res = await fetch("/api/products/export", {
        credentials: "include",
        headers: clientRequestHeaders(tid),
      });
      if (!res.ok) throw new Error("Export falló");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "productos.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch {
      toast.error("No se pudo exportar");
    }
  };
  const filterDepsKey = [
    search,
    filterCat,
    filterStockStatus,
    filterTrackStock,
    filterAddon,
    filterActivo,
    filterBrand,
    filterTipoVenta,
    filterProductType,
    filterPersonalizable,
    filterBundleOnly,
    filterPriceAlert,
    filterTag,
  ].join("|");

  useEffect(() => { setPage(1); }, [filterDepsKey]);
  useEffect(() => { loadProducts(); }, [tid, filterDepsKey, page]);

  // Renata → aplicar texto generado en el campo del formulario abierto
  useEffect(() => {
    function applyFieldValue(field: string, value: string) {
      setForm((prev) => {
        if (field === "description" || field === "descripcion" || field === "descripcion_nueva") {
          return { ...prev, description: value };
        }
        if (
          field === "longDescription" ||
          field === "descripcion_larga" ||
          field === "long_description"
        ) {
          return { ...prev, longDescription: value };
        }
        if (field === "name" || field === "nombre" || field === "nombre_nuevo") {
          return { ...prev, name: value };
        }
        return prev;
      });
      toast.success(`Texto aplicado en la ${fieldLabelEs(field)}`);
    }

    function onApplyField(ev: Event) {
      const detail = (ev as CustomEvent<{ field?: string; value?: string }>).detail;
      const field = detail?.field;
      const value = detail?.value;
      if (!field || typeof value !== "string") return;
      applyFieldValue(field, value);
      consumeRenataFieldApply();
    }

    // Si Renata aplicó mientras el form no estaba montado, recuperar al volver.
    const pending = consumeRenataFieldApply({ entity: "product" });
    if (pending?.field && typeof pending.value === "string") {
      applyFieldValue(pending.field, pending.value);
    }

    window.addEventListener("sm-renata-apply-field", onApplyField as EventListener);
    return () => {
      window.removeEventListener("sm-renata-apply-field", onApplyField as EventListener);
    };
  }, []);

  // Renata: producto abierto en el drawer (sin forzar Guardar al usar el FAB)
  useEffect(() => {
    if (!editorOpen) {
      clearRenataWorkspaceFocus();
      return;
    }
    // field/intent los setea RenataLauncher; preservarlos al refrescar el draft.
    const prev = getRenataWorkspaceFocus();
    setRenataWorkspaceFocus({
      route: "/app/productos",
      entity: "product",
      entityId: editing?.id ?? null,
      entityName: form.name.trim() || editing?.name || null,
      selection: editing?.id ? [editing.id] : [],
      field: prev?.entity === "product" ? prev.field : undefined,
      intent: prev?.entity === "product" ? prev.intent : undefined,
      draft: {
        name: form.name,
        description: form.description,
        longDescription: form.longDescription,
        precioUnitario: form.precioUnitario,
        precioPorKg: form.precioPorKg,
        tipoVenta: form.tipoVenta,
        productType: form.productType,
        tags: form.tags,
      },
    });
    return () => clearRenataWorkspaceFocus();
  }, [
    editorOpen,
    editing?.id,
    editing?.name,
    form.name,
    form.description,
    form.longDescription,
    form.precioUnitario,
    form.precioPorKg,
    form.tipoVenta,
    form.productType,
    form.tags,
  ]);

  const openCreate = () => {
    loadCats();
    loadBrands();
    setEditing(null);
    setForm(EMPTY_FORM);
    setEditorTab("esencial");
    setEditorOpen(true);
  };

  const openEdit = (p: Product) => {
    setEditing(p);
    setForm({
      name:                     p.name,
      description:              p.description ?? "",
      longDescription:          p.longDescription ?? "",
      categoriaId:              p.categoriaId ?? "",
      brandId:                  p.brandId ?? "",
      tipoVenta:                p.tipoVenta,
      sku:                      p.sku ?? "",
      ean:                      p.ean ?? "",
      priceMode:                p.priceMode === "net" ? "net" : "final",
      taxCategory:              p.taxCategory ?? "taxable",
      taxRate:                  p.taxRate != null ? String(p.taxRate) : "",
      costoReferencia:          p.costoReferencia != null ? String(p.costoReferencia) : "",
      markupPct:                p.markupPct != null ? String(p.markupPct) : "",
      costSourceProductId:      p.costSourceProductId ?? "",
      costMultiplier:           p.costMultiplier != null ? String(p.costMultiplier) : "",
      precioUnitario:           p.precioUnitario?.toString() ?? "",
      precioPorKg:              p.precioPorKg?.toString() ?? "",
      unidadMedida:             p.unidadMedida,
      minQty:                   p.minQty?.toString() ?? "",
      qtyStep:                  p.qtyStep?.toString() ?? "",
      stockActual:              p.stockActual?.toString() ?? "",
      stockMinimo:              p.stockMinimo?.toString() ?? "",
      imagenUrl:                p.imagenUrl ?? "",
      tags:                     [...p.tags],
      tiempoPreparacionMinutos: p.tiempoPreparacionMinutos?.toString() ?? "",
      sortOrder:                p.sortOrder.toString(),
      trackStock:               p.trackStock ?? false,
      isAddon:                  p.isAddon ?? false,
      bundleOnly:               p.bundleOnly ?? false,
      isPersonalizable:         p.isPersonalizable ?? false,
      variantAxes:              p.variantAxes ?? [],
      weightGrams:              p.weightGrams?.toString() ?? "",
      productType:              p.productType ?? "physical",
      sizeGuideId:              p.sizeGuideId ?? "",
    });
    setEditorTab("esencial");
    setCostSourceName(null);
    setCostSourceCost(null);
    if (p.costSourceProductId && tid) {
      apiGetJson<{ name: string; costoReferencia: number | null }>(`/api/products?id=${p.costSourceProductId}`, tid)
        .then((src) => {
          setCostSourceName(src.name);
          setCostSourceCost(src.costoReferencia ?? null);
        })
        .catch(() => setCostSourceName(null));
    }
    setEditorOpen(true);
  };

  const handleSave = async () => {
    if (!tid) return;
    if (!form.name.trim()) { toast.error("El nombre es requerido"); return; }
    if (form.tipoVenta === "unidad" && !form.precioUnitario) {
      toast.error("El precio unitario es requerido"); return;
    }
    if (form.tipoVenta === "peso" && !form.precioPorKg) {
      toast.error("El precio por kg es requerido"); return;
    }

    setSaving(true);
    try {
      const payload = {
        name:                     form.name.trim(),
        description:              form.description.trim() || null,
        longDescription:          form.longDescription.trim() || null,
        categoriaId:              form.categoriaId || null,
        brandId:                  form.brandId || null,
        tipoVenta:                form.tipoVenta,
        sku:                      form.sku.trim() || null,
        ean:                      form.ean.trim() || null,
        priceMode:                form.priceMode,
        taxCategory:              form.taxCategory,
        taxRate:                  form.taxRate.trim() ? parseFloat(form.taxRate) : null,
        costoReferencia:          form.costoReferencia.trim() ? parseFloat(form.costoReferencia) : null,
        markupPct:                form.markupPct.trim() ? parseFloat(form.markupPct) : null,
        costSourceProductId:      form.costSourceProductId || null,
        costMultiplier:           form.costMultiplier.trim() ? parseFloat(form.costMultiplier) : null,
        precioUnitario:           form.precioUnitario ? parseFloat(form.precioUnitario) : null,
        precioPorKg:              form.precioPorKg ? parseFloat(form.precioPorKg) : null,
        unidadMedida:             form.unidadMedida,
        // SCRUM-403/404: "." y "," son el mismo separador decimal acá — no
        // parseFloat directo, que trunca "15,28" en 15 sin avisar.
        minQty:                   form.minQty.trim() ? parseDecimalQtyInput(form.minQty) : null,
        qtyStep:                  form.qtyStep.trim() ? parseDecimalQtyInput(form.qtyStep) : null,
        stockActual:              form.stockActual.trim() ? parseDecimalQtyInput(form.stockActual) : null,
        stockMinimo:              form.stockMinimo.trim() ? parseDecimalQtyInput(form.stockMinimo) : null,
        imagenUrl:                form.imagenUrl.trim() || null,
        tags:                     form.tags,
        tiempoPreparacionMinutos: form.tiempoPreparacionMinutos ? parseInt(form.tiempoPreparacionMinutos) : 0,
        sortOrder:                parseInt(form.sortOrder) || 0,
        trackStock:               form.trackStock,
        isAddon:                  form.isAddon,
        bundleOnly:               form.bundleOnly,
        isPersonalizable:         form.isPersonalizable,
        variantAxes:              form.variantAxes,
        weightGrams:              form.weightGrams.trim() ? parseInt(form.weightGrams, 10) : null,
        productType:              form.productType,
        sizeGuideId:              form.sizeGuideId || null,
      };

      if (editing) {
        // PATCH uses snake_case keys for the patch function
        await apiSendJson(`/api/products?id=${editing.id}`, "PATCH", {
          name:                      payload.name,
          description:               payload.description,
          long_description:          payload.longDescription,
          categoria_id:              payload.categoriaId,
          brand_id:                  payload.brandId,
          tipo_venta:                payload.tipoVenta,
          sku:                       payload.sku,
          ean:                       payload.ean,
          price_mode:                payload.priceMode,
          tax_category:              payload.taxCategory,
          tax_rate:                  payload.taxRate,
          costo_referencia:          payload.costoReferencia,
          markup_pct:                payload.markupPct,
          cost_source_product_id:    payload.costSourceProductId,
          cost_multiplier:           payload.costMultiplier,
          precio_unitario:           payload.precioUnitario,
          precio_por_kg:             payload.precioPorKg,
          unidad_medida:             payload.unidadMedida,
          min_qty:                   payload.minQty,
          qty_step:                  payload.qtyStep,
          stock_actual:              payload.stockActual,
          stock_minimo:              payload.stockMinimo,
          // SCRUM-473: NO se manda imagen_url acá cuando se edita — mientras
          // el producto existe, ProductGalleryField es la única fuente de
          // verdad de la imagen (persiste vía sus propios llamados a
          // /api/media-library al momento de agregar/reordenar, no vía este
          // guardado). form.imagenUrl queda desactualizado apenas se usa la
          // galería (nadie la sincroniza de vuelta al estado del form), así
          // que mandarla acá pisaba con un valor viejo/vacío la imagen recién
          // asociada — causa raíz real del bug reproducido (imagen_url en
          // NULL con sm_media_links/media_id todavía apuntando a la imagen
          // correcta). El fallback de lectura en rowToProduct (galería >
          // columna cruda) ya protege contra esto en cualquier caso.
          tags:                      payload.tags,
          tiempo_preparacion_minutos: payload.tiempoPreparacionMinutos,
          sort_order:                payload.sortOrder,
          track_stock:               payload.trackStock,
          is_addon:                  payload.isAddon,
          bundle_only:               payload.bundleOnly,
          is_personalizable:         payload.isPersonalizable,
          variant_axes:              payload.variantAxes,
          weight_grams:              payload.weightGrams,
          product_type:              payload.productType,
          size_guide_id:             payload.sizeGuideId,
        }, tid);
        toast.success("Producto actualizado");
      } else {
        await apiSendJson("/api/products", "POST", payload, tid);
        toast.success("Producto creado");
      }
      setEditorOpen(false);
      loadProducts();
    } catch (e: unknown) {
      const err = e as Error & { quota?: QuotaLimitPayload; planLock?: PlanApiErrorPayload };
      if (err.quota) { setQuotaModal(err.quota); return; }
      if (err.planLock) { setPlanLock(err.planLock); return; }
      toast.error(err instanceof Error ? err.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const createAndAssociateSizeGuide = async () => {
    if (!tid || !newSizeGuideName.trim()) return;
    setSavingSizeGuide(true);
    try {
      const { guide } = await apiSendJson<{ guide: SizeGuideOption }>(
        "/api/size-guides",
        "POST",
        {
          name: newSizeGuideName.trim(),
          unit: "cm",
          content: { columns: ["Talle", "Pecho", "Largo"], rows: [{ values: ["S", "", ""] }, { values: ["M", "", ""] }, { values: ["L", "", ""] }] },
        },
        tid,
      );
      setForm((f) => ({ ...f, sizeGuideId: guide.id }));
      setSizeGuides((prev) => [...prev, guide]);
      setCreatingSizeGuide(false);
      setNewSizeGuideName("");
      toast.success("Guía creada y asociada — completá los valores desde Guías de talles");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo crear la guía");
    } finally {
      setSavingSizeGuide(false);
    }
  };

  const handleToggleActive = async (p: Product) => {
    if (!tid) return;
    try {
      await apiSendJson(`/api/products?id=${p.id}`, "PATCH", { activo: !p.activo }, tid);
      setProducts(prev => prev.map(x => x.id === p.id ? { ...x, activo: !x.activo } : x));
    } catch {
      toast.error("Error al actualizar estado");
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || !tid) return;
    try {
      await apiDelete(`/api/products?id=${deleteTarget.id}`, tid);
      toast.success("Producto eliminado");
      setProducts(prev => prev.filter(p => p.id !== deleteTarget.id));
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Error al eliminar");
    } finally {
      setDeleteTarget(null);
    }
  };

  const formCosto = form.costoReferencia.trim() ? parseFloat(form.costoReferencia) : null;
  const formMarkup = form.markupPct.trim() ? parseFloat(form.markupPct) : null;
  const suggestedPrice =
    formCosto != null && formCosto > 0 && formMarkup != null && formMarkup >= 0
      ? roundPrice(Math.round(formCosto * (1 + formMarkup / 100) * 100) / 100, priceRounding)
      : null;

  const pricingAlerts: PricingAlert[] = useMemo(
    () =>
      evaluateProductPricing({
        costoReferencia: formCosto,
        markupPct: formMarkup,
        precioUnitario: form.precioUnitario.trim() ? parseFloat(form.precioUnitario) : null,
        precioPorKg: form.precioPorKg.trim() ? parseFloat(form.precioPorKg) : null,
        tipoVenta: form.tipoVenta,
        priceMode: form.priceMode,
        taxCategory: form.taxCategory,
        taxRate: form.taxRate.trim() ? parseFloat(form.taxRate) : null,
      }),
    [formCosto, formMarkup, form.precioUnitario, form.precioPorKg, form.tipoVenta, form.priceMode, form.taxCategory, form.taxRate],
  );

  // SCRUM-402: on-input calculation — Costo/Markup actualizan "Precio final"
  // en cada tecla, sin CTA de recálculo/confirmación intermedio. Se aplica de
  // forma imperativa en los onChange de Costo/Markup (no vía efecto) para que
  // NUNCA se dispare al abrir una ficha existente y pisar un precio cargado
  // manualmente — solo corre ante una edición real del usuario.
  const applyCostMarkupPrice = (
    f: typeof form,
    costoStr: string,
    markupStr: string,
  ): Partial<typeof form> => {
    const costo = costoStr.trim() ? parseFloat(costoStr) : null;
    const markup = markupStr.trim() ? parseFloat(markupStr) : null;
    if (costo == null || !(costo > 0) || markup == null || !(markup >= 0)) return {};
    const price = roundPrice(Math.round(costo * (1 + markup / 100) * 100) / 100, priceRounding);
    if (price == null) return {};
    return f.tipoVenta === "peso" ? { precioPorKg: String(price) } : { precioUnitario: String(price) };
  };

  // ── SCRUM-105: costo derivado (producto origen × multiplicador) ──
  useEffect(() => {
    if (!editorOpen || !tid) return;
    const q = costSourceQuery.trim();
    if (q.length < 2) {
      setCostSourceMatches([]);
      setCostSourceSearching(false);
      return;
    }
    setCostSourceSearching(true);
    const t = setTimeout(() => {
      apiGetJson<ProductsPageResponse>(`/api/products?q=${encodeURIComponent(q)}&limit=8`, tid)
        .then((res) =>
          setCostSourceMatches(
            (res.items ?? [])
              .filter((p) => p.id !== editing?.id)
              .map((p) => ({ id: p.id, name: p.name, costoReferencia: p.costoReferencia ?? null })),
          ),
        )
        .catch(() => setCostSourceMatches([]))
        .finally(() => setCostSourceSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [costSourceQuery, tid, editorOpen, editing?.id]);

  const pickCostSource = (p: { id: string; name: string; costoReferencia: number | null }) => {
    // Costo propio y costo derivado son mutuamente excluyentes — elegir un
    // origen limpia el costo propio para evitar guardar ambos a la vez.
    setForm((f) => ({ ...f, costSourceProductId: p.id, costoReferencia: "" }));
    setCostSourceName(p.name);
    setCostSourceCost(p.costoReferencia);
    setShowCostSourceSuggestions(false);
    setCostSourceMatches([]);
    setCostSourceQuery("");
  };

  const clearCostSource = () => {
    setForm((f) => ({ ...f, costSourceProductId: "", costMultiplier: "" }));
    setCostSourceName(null);
    setCostSourceCost(null);
  };

  const derivedCostPreview =
    costSourceCost != null && form.costMultiplier.trim()
      ? Math.round(costSourceCost * (parseFloat(form.costMultiplier) || 0) * 10_000) / 10_000
      : null;

  // SCRUM-357: el campo Costo debe reflejar siempre el costo derivado vigente
  // mientras exista una derivación — no queda como un preview de solo texto.
  // SCRUM-402: si además hay markup cargado, el precio de venta se recalcula
  // en el mismo paso — para costo derivado, "siempre resincronizar" ya era el
  // comportamiento aceptado de este efecto (a diferencia del costo propio,
  // que solo recalcula ante una tecla real del usuario vía applyCostMarkupPrice).
  useEffect(() => {
    if (!form.costSourceProductId) return;
    const next = derivedCostPreview != null ? String(derivedCostPreview) : "";
    setForm((f) => {
      if (f.costoReferencia === next) return f;
      return { ...f, costoReferencia: next, ...applyCostMarkupPrice(f, next, f.markupPct) };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.costSourceProductId, derivedCostPreview]);

  // ── SCRUM-105: listas de precios en las que participa este producto ──
  useEffect(() => {
    if (!editorOpen || !editing?.id || !tid) {
      setChannelMemberships([]);
      return;
    }
    setMembershipsLoading(true);
    apiGetJson<{ memberships: ChannelMembershipForProduct[] }>(
      `/api/b2b/channels?productMemberships=${editing.id}`,
      tid,
    )
      .then((res) => setChannelMemberships(res.memberships ?? []))
      .catch(() => setChannelMemberships([]))
      .finally(() => setMembershipsLoading(false));
  }, [editorOpen, editing?.id, tid]);

  const toggleChannelMembership = async (m: ChannelMembershipForProduct) => {
    if (!tid || !editing?.id) return;
    try {
      if (m.itemId) {
        await apiDelete(`/api/b2b/channels?id=${m.channelId}&itemId=${m.itemId}`, tid);
        toast.success(`Quitado de "${m.channelName}"`);
      } else {
        await apiSendJson(
          "/api/b2b/channels",
          "POST",
          { action: "add_item", channelId: m.channelId, productId: editing.id },
          tid,
        );
        toast.success(`Agregado a "${m.channelName}"`);
      }
      const res = await apiGetJson<{ memberships: ChannelMembershipForProduct[] }>(
        `/api/b2b/channels?productMemberships=${editing.id}`,
        tid,
      );
      setChannelMemberships(res.memberships ?? []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo actualizar la lista de precios");
    }
  };

  // Filtered list (search is server-side, but filter active here too)
  const displayed = products;


  const columns: DataTableColumn<Product>[] = [
    {
      key: "product",
      header: "Producto",
      render: (p) => (
        <div className={`flex min-w-0 items-center gap-2 ${!p.activo ? "opacity-50" : ""}`}>
          <button
            type="button"
            title="Gestionar imágenes"
            onClick={(e) => {
              e.stopPropagation();
              setGalleryPickerProduct(p);
            }}
            className="shrink-0 rounded transition-opacity hover:opacity-80"
          >
            {p.imagenUrl ? (
              <img src={p.imagenUrl} alt="" className="h-9 w-9 rounded object-cover" />
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded bg-muted">
                <Package className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
          </button>
          <div className="min-w-0">
            <p className="truncate font-medium">{p.name}</p>
            {p.tags.length > 0 && (
              <div className="mt-0.5 flex flex-wrap gap-1">
                {p.tags.slice(0, 2).map((t) => (
                  <Badge key={t} variant="secondary" className="px-1 py-0 text-[10px]">
                    {t}
                  </Badge>
                ))}
                {p.tags.length > 2 && (
                  <Badge variant="secondary" className="px-1 py-0 text-[10px]">
                    +{p.tags.length - 2}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "categoria",
      header: "Categoría",
      className: "hidden md:table-cell text-muted-foreground",
      render: (p) => p.categoriaName ?? <span className="text-xs italic">Sin categoría</span>,
    },
    {
      key: "marca",
      header: "Marca",
      className: "hidden lg:table-cell text-muted-foreground",
      render: (p) => p.brandName ?? "—",
    },
    {
      key: "tipo",
      header: "Tipo",
      className: "hidden lg:table-cell",
      render: (p) =>
        p.tipoVenta === "peso" ? (
          <span className="flex items-center gap-1 text-xs font-medium text-orange-600">
            <Scale className="h-3 w-3" /> Peso
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs font-medium text-blue-600">
            <Package className="h-3 w-3" /> Unidad
          </span>
        ),
    },
    {
      key: "precio",
      header: "Precio",
      className: "text-right font-medium tabular-nums",
      render: (p) =>
        p.tipoVenta === "peso" ? (
          <>
            {pesos(p.precioPorKg)}
            <span className="text-xs text-muted-foreground">/kg</span>
          </>
        ) : (
          pesos(p.precioUnitario)
        ),
    },
    {
      key: "stock",
      header: "Stock",
      className: "hidden sm:table-cell text-center tabular-nums",
      render: (p) =>
        p.stockActual !== null ? (
          <span
            className={
              p.stockMinimo != null && p.stockActual <= p.stockMinimo
                ? "font-medium text-amber-600"
                : "text-muted-foreground"
            }
          >
            {p.stockActual} {p.tipoVenta === "peso" ? p.unidadMedida : "u."}
          </span>
        ) : (
          "—"
        ),
    },
    {
      key: "activo",
      header: "Activo",
      className: "text-center",
      render: (p) => (
        <Switch
          checked={p.activo}
          onCheckedChange={() => handleToggleActive(p)}
          onClick={(e) => e.stopPropagation()}
          aria-label="Activo"
        />
      ),
    },
    {
      key: "actions",
      header: "",
      className: "text-right",
      render: (p) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEdit(p); }}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-destructive hover:text-destructive"
            onClick={(e) => { e.stopPropagation(); setDeleteTarget(p); }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <ProductosTour />
      <MerchantExperienceBar screenId="productos" />
      <CrudShellHeader
        title="Productos"
        description="Catálogo completo de tu negocio."
        backHref="/app/catalogacion"
        backLabel="Volver a Catalogación"
        count={productsTotal}
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => void exportCsv()}><Download className="mr-2 h-4 w-4" />Exportar CSV</Button>
            {hideUnitStock
              ? <Button variant="outline" asChild><Link to="/app/ingredientes"><Layers className="mr-2 h-4 w-4" />Insumos</Link></Button>
              : <Button variant="outline" asChild><Link to="/app/catalogacion/importar"><Layers className="mr-2 h-4 w-4" />Inventario CSV</Link></Button>
            }
            <Button variant="outline" asChild><Link to="/app/inventario" search={{} as never}><Warehouse className="mr-2 h-4 w-4" />{inventoryNavLabel(stockMode)}</Link></Button>
            {canMutate && <Button onClick={openCreate} data-tour="productos-new-btn"><Plus className="mr-2 h-4 w-4" />Nuevo producto</Button>}
          </div>
        }
      />

      <ClinicalPageShell offset="none" className="space-y-6 pt-6">
      {/* List + editor lateral (sin modal) */}
      <WorkspaceMasterDetail
        variant="drawer"
        detailOpen={editorOpen}
        onDetailOpenChange={(open) => {
          if (!open) {
            setEditorOpen(false);
            setEditing(null);
            clearRenataWorkspaceFocus();
          }
        }}
        detailTitle={editing ? editing.name : "Nuevo producto"}
        list={
          <>
      {/* Filters */}
      <div className="space-y-3 border-b p-4" data-tour="productos-search">
        <FilterBar
          search={{ value: search, onChange: setSearch, placeholder: "Buscar por nombre..." }}
        >
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Categoría" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las categorías</SelectItem>
              {cats.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStockStatus} onValueChange={(v) => setFilterStockStatus(v as typeof filterStockStatus)}>
            <SelectTrigger className="w-full sm:w-[150px]">
              <SelectValue placeholder="Stock" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todo el stock</SelectItem>
              <SelectItem value="out">Sin stock</SelectItem>
              <SelectItem value="in">Con stock</SelectItem>
              <SelectItem value="low">Stock bajo</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterTrackStock} onValueChange={(v) => setFilterTrackStock(v as typeof filterTrackStock)}>
            <SelectTrigger className="w-full sm:w-[170px]">
              <SelectValue placeholder="Control stock" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Control de stock</SelectItem>
              <SelectItem value="yes">Con control</SelectItem>
              <SelectItem value="no">Sin control</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterAddon} onValueChange={(v) => setFilterAddon(v as typeof filterAddon)}>
            <SelectTrigger className="w-full sm:w-[150px]">
              <SelectValue placeholder="Add-ons" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Add-ons: todos</SelectItem>
              <SelectItem value="yes">Solo add-ons</SelectItem>
              <SelectItem value="no">Sin add-ons</SelectItem>
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant={advancedOpen || hasExtraFilters ? "secondary" : "outline"}
            size="sm"
            className="gap-1.5"
            onClick={() => setAdvancedOpen((o) => !o)}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            Avanzados
            {hasExtraFilters ? (
              <Badge variant="secondary" className="ml-0.5 h-5 px-1.5 text-[10px]">
                on
              </Badge>
            ) : null}
          </Button>
          {hasAnyFilter ? (
            <Button type="button" variant="ghost" size="sm" className="gap-1 text-muted-foreground" onClick={clearProductFilters}>
              <X className="h-3.5 w-3.5" /> Limpiar
            </Button>
          ) : null}
        </FilterBar>

        {advancedOpen ? (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/30 p-3">
            <Select value={filterActivo} onValueChange={(v) => setFilterActivo(v as typeof filterActivo)}>
              <SelectTrigger className="w-full sm:w-[140px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Activos e inactivos</SelectItem>
                <SelectItem value="true">Solo activos</SelectItem>
                <SelectItem value="false">Solo inactivos</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterBrand} onValueChange={setFilterBrand}>
              <SelectTrigger className="w-full sm:w-[160px]">
                <SelectValue placeholder="Marca" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las marcas</SelectItem>
                {brands.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterTipoVenta} onValueChange={(v) => setFilterTipoVenta(v as typeof filterTipoVenta)}>
              <SelectTrigger className="w-full sm:w-[140px]">
                <SelectValue placeholder="Tipo venta" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Unidad y peso</SelectItem>
                <SelectItem value="unidad">Por unidad</SelectItem>
                <SelectItem value="peso">Por peso</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterProductType} onValueChange={(v) => setFilterProductType(v as typeof filterProductType)}>
              <SelectTrigger className="w-full sm:w-[150px]">
                <SelectValue placeholder="Tipo producto" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                <SelectItem value="physical">Físico</SelectItem>
                <SelectItem value="digital">Digital</SelectItem>
                <SelectItem value="service">Servicio</SelectItem>
                <SelectItem value="hybrid">Híbrido</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPersonalizable} onValueChange={(v) => setFilterPersonalizable(v as typeof filterPersonalizable)}>
              <SelectTrigger className="w-full sm:w-[160px]">
                <SelectValue placeholder="Personalizable" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Personalizable: todos</SelectItem>
                <SelectItem value="yes">Personalizables</SelectItem>
                <SelectItem value="no">No personalizables</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterBundleOnly} onValueChange={(v) => setFilterBundleOnly(v as typeof filterBundleOnly)}>
              <SelectTrigger className="w-full sm:w-[160px]">
                <SelectValue placeholder="Solo bundles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Combos: todos</SelectItem>
                <SelectItem value="yes">Solo en packs</SelectItem>
                <SelectItem value="no">Catálogo normal</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPriceAlert} onValueChange={(v) => setFilterPriceAlert(v as typeof filterPriceAlert)}>
              <SelectTrigger className="w-full sm:w-[190px]" data-testid="product-filter-price-alert">
                <SelectValue placeholder="Alertas de precio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alertas de precio: todas</SelectItem>
                <SelectItem value="yes">Con alerta de precio</SelectItem>
                <SelectItem value="no">Sin alerta de precio</SelectItem>
              </SelectContent>
            </Select>
            <Input
              value={filterTag}
              onChange={(e) => setFilterTag(e.target.value)}
              placeholder="Tag exacto…"
              className="w-full sm:w-[160px]"
            />
          </div>
        ) : null}
      </div>

      {/* List */}
      {loading ? (
        <Loading variant="list" />
      ) : (
        <>
        <p className="text-sm text-muted-foreground">
          Mostrando {displayed.length} de {productsTotal} productos
        </p>
        <DataTable
          columns={columns}
          rows={displayed}
          getRowKey={(p) => p.id}
          onRowClick={(p) => openEdit(p)}
          emptyState={
            <CrudEmptyState
              icon={<ShoppingBasket className="h-8 w-8" />}
              title={hasAnyFilter ? "No encontramos productos con ese filtro." : "Todavía no cargaste productos para vender."}
              description={
                hasAnyFilter
                  ? "Probá con otra búsqueda o limpiá los filtros."
                  : "Agregá tus primeros productos al catálogo para que tus clientes puedan pedirlos."
              }
              action={
                !hasAnyFilter ? (
                  <Button onClick={openCreate} variant="outline" size="sm" className="gap-2">
                    <Plus className="h-4 w-4" /> Crear primer producto
                  </Button>
                ) : undefined
              }
            />
          }
        />
        {displayed.length > 0 && (
          <div className="flex flex-col gap-3 rounded-lg border bg-card px-4 py-3">
            <p className="text-xs text-muted-foreground">
              Página {page} de {Math.max(1, Math.ceil(productsTotal / PRODUCTS_PAGE_SIZE))} · {productsTotal} producto{productsTotal !== 1 ? "s" : ""}
            </p>
            {productsTotal > PRODUCTS_PAGE_SIZE && (
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      className={page <= 1 ? "pointer-events-none opacity-50" : ""}
                      onClick={(e) => {
                        e.preventDefault();
                        setPage((p) => Math.max(1, p - 1));
                      }}
                    />
                  </PaginationItem>
                  {Array.from(
                    { length: Math.min(7, Math.ceil(productsTotal / PRODUCTS_PAGE_SIZE)) },
                    (_, i) => i + 1,
                  ).map((p) => (
                    <PaginationItem key={p}>
                      <PaginationLink
                        href="#"
                        isActive={p === page}
                        onClick={(e) => {
                          e.preventDefault();
                          setPage(p);
                        }}
                      >
                        {p}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      className={
                        page >= Math.ceil(productsTotal / PRODUCTS_PAGE_SIZE)
                          ? "pointer-events-none opacity-50"
                          : ""
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        setPage((p) => p + 1);
                      }}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </div>
        )}
        </>
      )}
          </>
        }
        detail={
          editorOpen ? (
            <div className="flex h-full min-h-0 flex-col" data-testid="product-editor">
              {planLock ? (
                <div className="p-4"><FeatureLockedState payload={planLock} compact /></div>
              ) : null}

              {/* SCRUM-150: progressive disclosure via tabs (Esencial / Avanzado / Contenido) */}
              <Tabs
                value={editorTab}
                onValueChange={(v) => setEditorTab(v as typeof editorTab)}
                className="flex min-h-0 flex-1 flex-col"
                data-testid="product-editor-tabs"
              >
                <TabsList className="mx-4 mt-3 shrink-0" data-testid="product-editor-tabslist">
                  <TabsTrigger value="esencial" data-testid="product-tab-esencial">Esencial</TabsTrigger>
                  <TabsTrigger value="avanzado" data-testid="product-tab-avanzado">Avanzado</TabsTrigger>
                  <TabsTrigger value="contenido" data-testid="product-tab-contenido">Contenido</TabsTrigger>
                </TabsList>

                {/* ── TAB ESENCIAL: lo que siempre se necesita ── */}
                <TabsContent
                  value="esencial"
                  className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4"
                  data-testid="product-tab-content-esencial"
                >
                  <WorkspaceSectionTitle title="General" description="Nombre y clasificación" />
                  <div className="grid gap-3">
                    <div className="grid gap-1.5">
                      <FieldLabel htmlFor="p-name" tipKey="product.name" required>Nombre</FieldLabel>
                      <Input id="p-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Ej: Queso Cremoso" autoFocus />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="grid gap-1.5">
                        <FieldLabel tipKey="product.category">Categoría <span className="font-normal text-muted-foreground">(opcional)</span></FieldLabel>
                        <Select value={form.categoriaId || "none"} onValueChange={v => setForm(f => ({ ...f, categoriaId: v === "none" ? "" : v }))}>
                          <SelectTrigger data-testid="product-category"><SelectValue placeholder="Sin categoría" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sin categoría</SelectItem>
                            {cats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-1.5">
                        <FieldLabel tipKey="product.brand">Marca <span className="font-normal text-muted-foreground">(opcional)</span></FieldLabel>
                        <Select value={form.brandId || "none"} onValueChange={(v) => setForm((f) => ({ ...f, brandId: v === "none" ? "" : v }))}>
                          <SelectTrigger><SelectValue placeholder="Sin marca" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sin marca</SelectItem>
                            {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {tid && suppliersEnabled && editing ? (
                    <ProductSupplierPanel tenantId={tid} productId={editing.id} />
                  ) : null}

                  {/* SCRUM-402: Costo y margen (+ Costo derivado) reubicado a Esencial,
                      entre Proveedores y Precio — on-input calculation, sin CTA de
                      recálculo/confirmación intermedio. */}
                  <div className="rounded-lg border border-border/60 bg-muted/20 p-3 space-y-3">
                    <div>
                      <p className="text-xs font-medium">Costo y margen (opcional)</p>
                      <p className="text-[11px] text-muted-foreground">
                        Cargá el costo y el markup para calcular el precio final automáticamente.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <label className="text-xs text-muted-foreground">Costo</label>
                        <Input
                          type="number" min={0} step={0.01}
                          value={form.costoReferencia}
                          disabled={!!form.costSourceProductId}
                          onChange={(e) => {
                            const v = e.target.value;
                            setForm((f) => ({
                              ...f,
                              costoReferencia: v,
                              ...(v.trim() ? { costSourceProductId: "", costMultiplier: "" } : {}),
                              ...applyCostMarkupPrice(f, v, f.markupPct),
                            }));
                            if (v.trim()) { setCostSourceName(null); setCostSourceCost(null); }
                          }}
                          placeholder="$"
                          data-testid="product-cost"
                        />
                        {form.costSourceProductId ? (
                          <p className="text-[11px] text-muted-foreground">Derivado de {costSourceName ?? "otro producto"} — quitá la derivación para editarlo manualmente.</p>
                        ) : null}
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs text-muted-foreground">Markup %</label>
                        <Input
                          type="number" min={0} step={0.01}
                          value={form.markupPct}
                          onChange={(e) => {
                            const v = e.target.value;
                            setForm((f) => ({ ...f, markupPct: v, ...applyCostMarkupPrice(f, f.costoReferencia, v) }));
                          }}
                          placeholder="Ej. 30"
                          data-testid="product-markup"
                        />
                      </div>
                    </div>

                    {suggestedPrice != null ? (
                      <p className="text-xs text-muted-foreground" data-testid="product-suggested-price">
                        Precio final actualizado automáticamente: <strong className="text-foreground">${suggestedPrice.toFixed(2)}</strong>
                      </p>
                    ) : null}

                    {pricingAlerts.length > 0 ? (
                      <div className="space-y-1.5" data-testid="product-pricing-alerts">
                        {pricingAlerts.map((a) => (
                          <p
                            key={a.code}
                            className={`flex items-start gap-1.5 text-xs ${a.severity === "warning" ? "text-amber-500" : "text-muted-foreground"}`}
                          >
                            {a.severity === "warning" ? (
                              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            ) : (
                              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            )}
                            {a.message}
                          </p>
                        ))}
                      </div>
                    ) : null}

                    <div className="border-t border-border/50 pt-3 space-y-2">
                      <p className="text-xs font-medium">Costo derivado (opcional)</p>
                      <p className="text-[11px] text-muted-foreground">
                        En vez de cargar un costo propio, calculalo a partir de otro producto (ej. una caja) × un multiplicador (ej. una unidad fraccionada).
                      </p>
                      {form.costSourceProductId && costSourceName ? (
                        <div className="rounded-md border border-border/60 bg-card p-2.5 space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="flex items-center gap-1.5 text-sm">
                              <Link2 className="h-3.5 w-3.5 text-primary" />
                              Deriva de: <strong>{costSourceName}</strong>
                            </span>
                            <button type="button" onClick={clearCostSource} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                              <Unlink className="h-3 w-3" /> Quitar
                            </button>
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-xs text-muted-foreground">Multiplicador</label>
                            <Input
                              type="number" min={0} step={0.0001}
                              value={form.costMultiplier}
                              onChange={(e) => setForm((f) => ({ ...f, costMultiplier: e.target.value }))}
                              placeholder="Ej. 0.02"
                              data-testid="product-cost-multiplier"
                            />
                          </div>
                          {derivedCostPreview != null ? (
                            <p className="text-xs text-muted-foreground">
                              Costo calculado: <strong className="text-foreground">${derivedCostPreview.toFixed(2)}</strong> (costo del origen × multiplicador)
                            </p>
                          ) : null}
                        </div>
                      ) : (
                        <div className="relative">
                          <Input
                            value={costSourceQuery}
                            onChange={(e) => { setCostSourceQuery(e.target.value); setShowCostSourceSuggestions(true); }}
                            onFocus={() => setShowCostSourceSuggestions(true)}
                            onBlur={() => setTimeout(() => setShowCostSourceSuggestions(false), 200)}
                            placeholder="Buscar producto origen…"
                            data-testid="product-cost-source-search"
                          />
                          {showCostSourceSuggestions && (costSourceSearching || costSourceMatches.length > 0) && (
                            <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-48 overflow-y-auto rounded-xl border border-border/60 bg-card shadow-lg">
                              {costSourceSearching ? (
                                <p className="px-3 py-2 text-xs text-muted-foreground">Buscando…</p>
                              ) : (
                                costSourceMatches.map((p) => (
                                  <button
                                    key={p.id}
                                    type="button"
                                    onClick={() => pickCostSource(p)}
                                    className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-primary/10"
                                  >
                                    <span>{p.name}</span>
                                    {p.costoReferencia != null ? (
                                      <span className="text-xs text-muted-foreground">costo ${p.costoReferencia}</span>
                                    ) : null}
                                  </button>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <WorkspaceSectionTitle title="Precio" description="Tipo de venta y precio de catálogo" />
                  <div className="grid gap-3">
                    <div className="grid grid-cols-2 gap-2">
                      {(["unidad", ...( ["fast_food","market","dietetics","restaurant","cafe"].includes(businessProfile) ? ["peso"] : [])] as TipoVenta[]).map(tipo => (
                        <button key={tipo} type="button" onClick={() => setForm(f => ({ ...f, tipoVenta: tipo }))}
                          className={`flex items-center gap-2 rounded-md border p-2.5 text-sm font-medium transition-colors ${form.tipoVenta === tipo ? "border-primary bg-primary/10 text-primary" : "hover:bg-muted"}`}>
                          {tipo === "unidad" ? <Package className="h-4 w-4" /> : <Scale className="h-4 w-4" />}
                          {tipo === "unidad" ? "Por unidad" : "Por peso"}
                        </button>
                      ))}
                    </div>
                    {form.tipoVenta === "unidad" ? (
                      <div className="space-y-1.5">
                        <FieldLabel tipKey="product.price" className="text-xs text-muted-foreground">{form.priceMode === "net" ? "Precio antes de IVA" : "Precio final"}</FieldLabel>
                        <Input type="number" min={0} step={0.01} value={form.precioUnitario} onChange={e => setForm(f => ({ ...f, precioUnitario: e.target.value }))} placeholder="$" data-testid="product-price" />
                      </div>
                    ) : (
                      <div className="grid gap-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <FieldLabel tipKey="product.price" className="text-xs text-muted-foreground">Precio por kg / unidad</FieldLabel>
                            <Input type="number" min={0} step={0.01} value={form.precioPorKg} onChange={e => setForm(f => ({ ...f, precioPorKg: e.target.value }))} placeholder="Precio / unidad ($)" />
                          </div>
                          <Select value={form.unidadMedida} onValueChange={v => setForm(f => ({ ...f, unidadMedida: v }))}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{UNIDADES_MEDIDA.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="grid gap-1.5">
                            <FieldLabel tipKey="product.saleType" className="text-xs text-muted-foreground">Peso / cant. mínima</FieldLabel>
                            <Input type="text" inputMode="decimal" value={form.minQty} onChange={(e) => setForm((f) => ({ ...f, minQty: sanitizeDecimalQtyInput(e.target.value) }))} placeholder="0.5" data-tour="product-min-qty" />
                          </div>
                          <div className="grid gap-1.5">
                            <FieldLabel tipKey="product.saleType" className="text-xs text-muted-foreground">Multiplicador (+/−)</FieldLabel>
                            <Input type="text" inputMode="decimal" value={form.qtyStep} onChange={(e) => setForm((f) => ({ ...f, qtyStep: sanitizeDecimalQtyInput(e.target.value) }))} placeholder="0.5" data-tour="product-qty-step" />
                          </div>
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          Ej. mínima 0.5 y multiplicador 0.5 → el cliente puede pedir 0.5, 1.0, 1.5 {form.unidadMedida}…
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Descripción corta e imagen también en Esencial */}
                  <WorkspaceSectionTitle title="Presentación" description="Descripción corta e imagen del producto" />
                  <div className="grid gap-1.5">
                    <div className="flex items-center justify-between">
                      <FieldLabel tipKey="product.description" className="text-xs text-muted-foreground">Descripción corta (opcional)</FieldLabel>
                      <RenataLauncher
                        entityType="product"
                        entityId={editing?.id}
                        entityName={form.name || editing?.name}
                        field="description"
                        intent={form.description.trim() ? "improve_text" : "create_field"}
                        currentValue={form.description}
                        mode="icon"
                        label={form.description.trim() ? "Mejorar descripción corta" : "Crear descripción corta"}
                      />
                    </div>
                    <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder={isGastro ? "Ej: Aceite de oliva, orégano, tomate" : "Ej: Descripción breve del producto"} />
                  </div>
                  {editing && !form.imagenUrl.trim() && tid && (
                    <SmartMatchChip
                      tenantId={tid}
                      entityType="product"
                      entityId={editing.id}
                      slug={editing.slug}
                      name={form.name || editing.name}
                      onLinked={(url) => setForm((f) => ({ ...f, imagenUrl: url }))}
                    />
                  )}
                  <FieldLabel tipKey="product.image">{editing ? "Imágenes (hasta 5)" : "Imagen"}</FieldLabel>
                  {editing && tid ? (
                    <ProductGalleryField tenantId={tid} productId={editing.id} />
                  ) : (
                    <ImageUploadField
                      kind="product"
                      value={form.imagenUrl.trim() || null}
                      onChange={(url) => setForm((f) => ({ ...f, imagenUrl: url ?? "" }))}
                      tenantId={tid}
                      disabled={saving}
                    />
                  )}
                </TabsContent>

                {/* ── TAB AVANZADO: IVA, costo, inventario, variantes ── */}
                <TabsContent
                  value="avanzado"
                  className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4"
                  data-testid="product-tab-content-avanzado"
                >
                  <WorkspaceSectionTitle title="Precios e IVA" description="Modo de precio, categoría fiscal y costo de referencia" />
                  <div className="grid gap-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <label className="text-xs text-muted-foreground">Modo de precio</label>
                        <Select value={form.priceMode} onValueChange={(v) => setForm((f) => ({ ...f, priceMode: v as "net" | "final" }))}>
                          <SelectTrigger data-testid="product-price-mode"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="final">Precio final (IVA incluido)</SelectItem>
                            <SelectItem value="net">Precio antes de IVA</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs text-muted-foreground">Categoría fiscal</label>
                        <Select value={form.taxCategory} onValueChange={(v) => setForm((f) => ({ ...f, taxCategory: v as typeof form.taxCategory }))}>
                          <SelectTrigger data-testid="product-tax-category"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="taxable">Gravado</SelectItem>
                            <SelectItem value="exempt">Exento</SelectItem>
                            <SelectItem value="non_taxable">No gravado</SelectItem>
                            <SelectItem value="not_subject">No alcanzado</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {form.taxCategory === "taxable" ? (
                      <div className="space-y-1.5">
                        <label className="text-xs text-muted-foreground">Alícuota IVA (%)</label>
                        <Input type="number" min={0} step={0.01} value={form.taxRate} onChange={(e) => setForm((f) => ({ ...f, taxRate: e.target.value }))} placeholder="Ej. 21" data-testid="product-tax-rate" />
                        <p className="text-[11px] text-muted-foreground">Si lo dejás vacío, se usa la alícuota del comercio al facturar.</p>
                      </div>
                    ) : null}
                  </div>

                  <WorkspaceSectionTitle title="Identificación" description="SKU y EAN/GTIN opcionales (únicos en tu comercio)" />
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <label className="text-xs text-muted-foreground">SKU</label>
                      <Input value={form.sku} onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))} placeholder="Ej. PAN-001" data-testid="product-sku" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs text-muted-foreground">EAN / GTIN</label>
                      <Input value={form.ean} onChange={(e) => setForm((f) => ({ ...f, ean: e.target.value }))} placeholder="Ej. 7790000000000" inputMode="numeric" data-testid="product-ean" />
                    </div>
                  </div>

                  <WorkspaceSectionTitle title="Tipo de producto" description="Físico, digital, servicio o híbrido" />
                  <Select
                    value={form.productType}
                    onValueChange={(v) => setForm((f) => ({ ...f, productType: v as ProductType }))}
                    data-tour="product-type"
                  >
                    <SelectTrigger data-testid="product-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(Object.keys(PRODUCT_TYPE_LABELS) as ProductType[]).map((pt) => (
                        <SelectItem key={pt} value={pt}>{PRODUCT_TYPE_LABELS[pt]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(form.productType === "digital" || form.productType === "service") && (
                    <p className="text-xs text-emerald-700 dark:text-emerald-400">
                      Producto digital — no requiere envío
                    </p>
                  )}

                  {sizeGuideEnabled ? (
                    <>
                      <WorkspaceSectionTitle title="Guía de talles" description="Opcional — solo si aplica a este producto" />
                      <div className="grid gap-2">
                        {!creatingSizeGuide ? (
                          <>
                            <Select
                              value={form.sizeGuideId || "none"}
                              onValueChange={(v) => {
                                if (v === "__create__") { setCreatingSizeGuide(true); return; }
                                setForm((f) => ({ ...f, sizeGuideId: v === "none" ? "" : v }));
                              }}
                            >
                              <SelectTrigger data-testid="product-size-guide"><SelectValue placeholder="Sin guía" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Sin guía</SelectItem>
                                {sizeGuides.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                                <SelectItem value="__create__">+ Crear nueva guía</SelectItem>
                              </SelectContent>
                            </Select>
                            {form.sizeGuideId && (
                              <a href="/app/guias-de-talles" target="_blank" rel="noopener noreferrer" className="text-xs text-primary underline-offset-2 hover:underline">
                                Editar el contenido de esta guía →
                              </a>
                            )}
                          </>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Input
                              value={newSizeGuideName}
                              onChange={(e) => setNewSizeGuideName(e.target.value)}
                              placeholder="Ej: Remeras / Hombre"
                              autoFocus
                            />
                            <Button size="sm" onClick={createAndAssociateSizeGuide} disabled={savingSizeGuide || !newSizeGuideName.trim()}>
                              {savingSizeGuide ? "Creando..." : "Crear y asociar"}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setCreatingSizeGuide(false); setNewSizeGuideName(""); }}>
                              Cancelar
                            </Button>
                          </div>
                        )}
                      </div>
                    </>
                  ) : null}

                  {!hideUnitStock ? (
                    <>
                      <WorkspaceSectionTitle
                        title="Inventario"
                        description={
                          stockMode === "hybrid"
                            ? "Stock del producto a la venta. Si usa receta, el stock también depende de los insumos."
                            : "Stock unitario del producto. Abastecimiento carga mercadería; la venta descuenta este stock."
                        }
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <div className="grid gap-1.5">
                          <FieldLabel tipKey="product.stock" className="text-xs text-muted-foreground">Stock actual (opcional)</FieldLabel>
                          <Input type="text" inputMode="decimal" value={form.stockActual} onChange={e => setForm(f => ({ ...f, stockActual: sanitizeDecimalQtyInput(e.target.value) }))} placeholder="—" data-testid="product-stock" />
                        </div>
                        <div className="grid gap-1.5">
                          <FieldLabel tipKey="product.stock" className="text-xs text-muted-foreground">Stock mínimo (opcional)</FieldLabel>
                          <Input type="text" inputMode="decimal" value={form.stockMinimo} onChange={e => setForm(f => ({ ...f, stockMinimo: sanitizeDecimalQtyInput(e.target.value) }))} placeholder="—" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground rounded-md border border-dashed border-border/70 bg-muted/20 px-3 py-2">
                      En gastronomía el stock vive en <strong>ingredientes</strong>. Definí la receta abajo; las entradas van por Abastecimiento → Recepciones y las mermas por Inventario.
                    </p>
                  )}

                  {productTypeRequiresShipping(form.productType) && (
                    <>
                      <WorkspaceSectionTitle title="Logística" description="Opcional — peso para cotizar envío" />
                      <div className="grid gap-1.5">
                        <FieldLabel tipKey="product.saleType" className="text-xs text-muted-foreground">Peso en gramos (opcional)</FieldLabel>
                        <Input
                          type="number"
                          min={0}
                          step={1}
                          value={form.weightGrams}
                          onChange={(e) => setForm((f) => ({ ...f, weightGrams: e.target.value }))}
                          placeholder="Ej: 500"
                          data-tour="product-weight-grams"
                        />
                      </div>
                    </>
                  )}

                  <WorkspaceSectionTitle
                    title={isGastro ? "Tags y extras" : "Tags y variantes"}
                    description={
                      isGastro
                        ? "En gastronomía usá extras y receta en lugar de variantes"
                        : "Etiquetas, variantes y extras del catálogo"
                    }
                  />
                  <div className="grid gap-1.5">
                    <FieldLabel tipKey="product.tags" className="text-xs text-muted-foreground">Tags (opcional)</FieldLabel>
                    <ProductTagPicker tenantId={tid} value={form.tags} onChange={(tags) => setForm((f) => ({ ...f, tags }))} disabled={saving} />
                  </div>
                  {tid && (
                    <ProductCatalogCoreSection
                      tid={tid}
                      productId={editing?.id ?? null}
                      trackStock={form.trackStock}
                      isAddon={form.isAddon}
                      bundleOnly={form.bundleOnly}
                      isPersonalizable={form.isPersonalizable}
                      merchantEnabled={merchantEnabled}
                      axes={form.variantAxes}
                      hideVariants={isGastro}
                      isGastro={isGastro}
                      onChangeTrackStock={(v) => setForm((f) => ({ ...f, trackStock: v }))}
                      onChangeIsAddon={(v) => setForm((f) => ({ ...f, isAddon: v, bundleOnly: v ? false : f.bundleOnly }))}
                      onChangeBundleOnly={(v) => setForm((f) => ({ ...f, bundleOnly: v }))}
                      onChangeIsPersonalizable={(v) => setForm((f) => ({ ...f, isPersonalizable: v }))}
                      onChangeAxes={(axes) => setForm((f) => ({ ...f, variantAxes: axes }))}
                    />
                  )}
                </TabsContent>

                {/* ── TAB CONTENIDO: textos, imagen, receta y listas de precios ── */}
                <TabsContent
                  value="contenido"
                  className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4"
                  data-testid="product-tab-content-contenido"
                >
                  <WorkspaceSectionTitle title="Textos" description="Descripciones para la ficha del producto" />
                  <div className="grid gap-1.5">
                    <div className="flex items-center justify-between">
                      <FieldLabel tipKey="product.description" className="text-xs text-muted-foreground">Descripción corta (opcional)</FieldLabel>
                      <RenataLauncher
                        entityType="product"
                        entityId={editing?.id}
                        entityName={form.name || editing?.name}
                        field="description"
                        intent={form.description.trim() ? "improve_text" : "create_field"}
                        currentValue={form.description}
                        mode="icon"
                        label={form.description.trim() ? "Mejorar descripción corta" : "Crear descripción corta"}
                      />
                    </div>
                    <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder={isGastro ? "Ej: Aceite de oliva, orégano, tomate" : "Ej: Descripción breve del producto"} />
                  </div>
                  <div className="grid gap-1.5">
                    <div className="flex items-center justify-between">
                      <FieldLabel tipKey="product.description" className="text-xs text-muted-foreground">Descripción larga (opcional)</FieldLabel>
                      <RenataLauncher
                        entityType="product"
                        entityId={editing?.id}
                        entityName={form.name || editing?.name}
                        field="longDescription"
                        intent={form.longDescription.trim() ? "expand" : "create_field"}
                        currentValue={form.longDescription}
                        mode="menu"
                        label={form.longDescription.trim() ? "Renata" : "Crear descripción larga"}
                      />
                    </div>
                    <Textarea rows={3} value={form.longDescription} onChange={e => setForm(f => ({ ...f, longDescription: e.target.value }))} placeholder="Detalle para la ficha del producto" />
                  </div>

                  <WorkspaceSectionTitle title="Imagen" description={editing ? "Imágenes (hasta 5) — misma galería que en la pestaña Esencial" : "Imagen principal del producto"} />
                  {editing && !form.imagenUrl.trim() && tid && (
                    <SmartMatchChip
                      tenantId={tid}
                      entityType="product"
                      entityId={editing.id}
                      slug={editing.slug}
                      name={form.name || editing.name}
                      onLinked={(url) => setForm((f) => ({ ...f, imagenUrl: url }))}
                    />
                  )}
                  {editing && tid ? (
                    <ProductGalleryField tenantId={tid} productId={editing.id} />
                  ) : (
                    <ImageUploadField
                      kind="product"
                      value={form.imagenUrl.trim() || null}
                      onChange={(url) => setForm((f) => ({ ...f, imagenUrl: url ?? "" }))}
                      tenantId={tid}
                      disabled={saving}
                    />
                  )}

                  {tid && editing && (
                    <DigitalAssetsSection
                      tenantId={tid}
                      productId={editing.id}
                      productType={form.productType}
                    />
                  )}

                  {editing && (
                    <div className="space-y-2">
                      <WorkspaceSectionTitle
                        title="Listas de precios"
                        description="Dónde se vende este producto además del precio público, y a qué precio."
                      />
                      {membershipsLoading ? (
                        <p className="text-xs text-muted-foreground">Cargando listas…</p>
                      ) : channelMemberships.length === 0 ? (
                        <p className="text-xs text-muted-foreground">Este comercio todavía no tiene listas de precios (Mayoristas, etc.) configuradas.</p>
                      ) : (
                        <div className="space-y-1.5" data-testid="product-price-lists">
                          {channelMemberships.map((m) => (
                            <div
                              key={m.channelId}
                              className={`flex items-center justify-between gap-3 rounded-lg border p-2.5 ${m.itemId ? "border-primary/40 bg-primary/5" : "border-border/60"}`}
                            >
                              <div className="min-w-0">
                                <p className="text-sm font-medium">{m.channelName}</p>
                                <p className="text-xs text-muted-foreground">
                                  {m.itemId
                                    ? m.resolvedPrice != null
                                      ? `Precio en esta lista: $${m.resolvedPrice.toFixed(2)} (${m.pricingMode === "inherit" ? `${m.discountPct}% desc.` : m.pricingMode === "markup" ? `markup ${m.markupPct}%` : m.pricingMode === "discount" ? `${m.itemDiscountPct}% desc. propio` : "precio fijo"})`
                                      : m.priceNote ?? "Sin precio calculable todavía"
                                    : `No incluido — ${m.discountPct}% de descuento si se agrega`}
                                </p>
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                variant={m.itemId ? "outline" : "secondary"}
                                onClick={() => void toggleChannelMembership(m)}
                              >
                                {m.itemId ? "Quitar" : "Agregar"}
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {tid && bomEnabled && isGastro && editing && !form.isAddon ? (
                    <ProductRecipePanel
                      tenantId={tid}
                      productId={editing.id}
                      productName={form.name || editing.name}
                    />
                  ) : null}
                  {tid && editing && !form.isAddon ? (
                    <ProductPresentationsPanel
                      tenantId={tid}
                      productId={editing.id}
                      baseUnitLabel={form.tipoVenta === "peso" ? form.unidadMedida : "unidad"}
                    />
                  ) : null}
                </TabsContent>
              </Tabs>

              <div className="shrink-0 flex gap-2 border-t border-border bg-background p-4">
                <Button variant="outline" className="flex-1" onClick={() => { setEditorOpen(false); setEditing(null); }} disabled={saving}>Cancelar</Button>
                <Button className="flex-1" onClick={handleSave} disabled={saving} data-testid="product-save">
                  {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {editing ? "Guardar" : "Crear"}
                </Button>
              </div>
            </div>
          ) : null
        }
      />

      </ClinicalPageShell>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={open => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar producto?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará <strong>{deleteTarget?.name}</strong> del catálogo.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <QuotaLimitModal open={quotaModal != null} payload={quotaModal} onClose={() => setQuotaModal(null)} />

      {/* SCRUM-414: gestión rápida de imágenes desde el listado */}
      <Dialog open={!!galleryPickerProduct} onOpenChange={(open) => !open && setGalleryPickerProduct(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Imágenes — {galleryPickerProduct?.name}</DialogTitle>
          </DialogHeader>
          {galleryPickerProduct && tid ? (
            <ProductGalleryField tenantId={tid} productId={galleryPickerProduct.id} />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
